import { create, v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/framework/core/middleware/theme';
import { createNlsMiddleware, keymap, pathmap, userstate, contentTrack, WhereYouLeftOff } from 'lcc-state';
import { ParentTree } from 'lcc-state/interfaces';

import { MicroCredentialCard } from '@holmescorp/card';
import { MicroCardButton, MicroProgress } from './index';

import css from '../css/where-you-left-off.m.css';

export interface MCWhereYouLeftOffProperties {
	topicKey?: string;
}

const nlsSettings = createNlsMiddleware('settings');
const factory = create({
	nlsSettings,
	userstate,
	contentTrack,
	theme
}).properties<MCWhereYouLeftOffProperties>();

export const MCWhereYouLeftOff = factory(
	({ id, properties, middleware: { nlsSettings, userstate, contentTrack, theme } }) => {
		const {
			messages: {
				wylo_header = '[settings] wylo_header',
				wylo_get_started = 'Get started',
				wylo_continue = 'Continue'
			}
		} = nlsSettings();
		const { topicKey = '' } = properties();

		const themeCss = theme.classes(css);

		const { get, path } = userstate;
		const tracking: WhereYouLeftOff = get(path('toolstate', 'trackingwylo'));
		const status: ParentTree = get(path('status'));

		const ttopic = tracking.tracking?.topic;
		const ttopicgroup = tracking.tracking?.topicgroup;
		const ttopicset = tracking.tracking?.topicset;

		if (!ttopicset) {
			return v('div', {}, []);
		}

		const onAction = () => {
			if (ttopicgroup === 'quiz' || ttopicgroup === 'flashcards') {
				const { path } = keymap.get(ttopicset)!;
				const { view } = pathmap.get(`${path}/${ttopicgroup}`)!;
				view();
			} else if (ttopicgroup) {
				const topic = status[ttopicgroup];
				const topicStatus = topic.data;
				const nextTopic = String(topic.next);
				const readingsComplete = topicStatus.complete === topicStatus.total;

				if (readingsComplete && nextTopic) {
					const { path } = keymap.get(nextTopic)!;
					const { view } = pathmap.get(path)!;
					view();
				} else {
					const { path } = keymap.get(ttopicgroup)!;
					const { view } = ttopic ? pathmap.get(`${path}/${ttopic}`)! : pathmap.get(path)!;

					view();
				}
			} else {
				const { path } = keymap.get(ttopicset)!;
				const { view } = pathmap.get(path)!;
				view();
			}
		};

		const { path: topicsetPath } = keymap.get(ttopicset)!;
		const { topic: tdetail } = pathmap.get(topicsetPath)!;

		const allTopicsets = Object.values(contentTrack.tracked.content).flat();
		const trackedTopicset = allTopicsets.find((topic) => topic.topicset === ttopicset);

		const topic = tdetail.topicset;
		const topicgroups = topic.children?.filter((child) => 'topicgroup' in child) || [];
		const readTime = topicgroups.reduce((acc, child) => {
			const time = child.topicgroup.attributes?.readtime || 0;
			return acc + time;
		}, 0);

		const totalComplete = topicgroups.reduce((count, child) => {
			const topicdata = status[child.topicgroup.attributes?.key || ''];
			if (topicdata.data.complete == topicdata.data.total) {
				count += 1;
			}
			return count;
		}, 0);

		const wylo_card = v(
			'div',
			{
				id,
				classes: [theme.variant(), themeCss.card]
			},
			[
				w(MicroCredentialCard, { xqa: 'micro-credential-card-wylo' }, [
					{
						leading: v('div', { classes: [themeCss.leading] }, [
							v('img', {
								title: topic.attributes?.title,
								src: `${window.hcstatic.portal_cdn}/js/images/${window.hcstatic.portal_site}/icons/icon_${ttopicset}.svg`
							})
						]),
						trailing: v('div', { classes: [themeCss.trailing] }, [
							v(
								'div',
								{ classes: [themeCss.alphaHalfBlack, themeCss.basefont], xqa: 'estimated-read-time' },
								[`${readTime} minutes`]
							),
							v('div', { classes: [themeCss.titlefont], xqa: 'progress-area-title' }, [
								topic.attributes?.title
							]),
							w(MicroProgress, {
								label: 'Reading Progress',
								value: totalComplete,
								min: 0,
								max: topicgroups.length
							}),
							v('div', { classes: [themeCss.actions] }, [
								topicKey !== ttopicset
									? w(MicroCardButton, {
											key: `card-button-${ttopicset}`,
											labelText: trackedTopicset?.isreadingstarted
												? wylo_continue
												: wylo_get_started,
											darkMode: true,
											onClick: (): void => {
												onAction();
											},
											onDown: (): void => {
												onAction();
											}
									  })
									: null
							])
						])
					}
				])
			]
		);

		return v('div', { key: 'mc-where-you-left-off', xqa: 'mc-where-you-left-off' }, [
			v('h1', { classes: [themeCss.tgHeader] }, [wylo_header]),
			v('div', { classes: [theme.variant()] }, [wylo_card])
		]);
	}
);

export default MCWhereYouLeftOff;
